/**
 * Admin Settings Page
 * Configure restaurant settings
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Save, Building2, Palette, Plug, Mail, Clock, Info, Euro } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { domainsMatchFromAndSmtp } from '@/utils/mailMatcher';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { adminRestaurantService } from '@/services/adminRestaurantService';
import { useAdminAuth } from '@/stores/adminAuthStore';
import { useRestaurant } from '@/stores/restaurantStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AdminPageShell, PageHeader, PageLoading, AdminButton } from '@/components/admin/shell';
import type { UpdateRestaurantRequest } from '@/types/restaurant';

// Validation schema
const settingsSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(255),
  logo_url: z.string().url('Debe ser una URL válida').optional().or(z.literal('')),
  primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Debe ser un color hexadecimal válido (ej: #b41f3d)').optional().or(z.literal('')),
  email_menu_title: z.string().max(100, 'Máximo 100 caracteres').optional().or(z.literal('')),
  email_logo_url: z.string().url('Debe ser una URL válida').optional().or(z.literal('')),
  provider: z.enum(['square']),
  lunch_start_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Formato inválido (HH:MM)').optional().or(z.literal('')),
  lunch_end_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Formato inválido (HH:MM)').optional().or(z.literal('')),
  dinner_start_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Formato inválido (HH:MM)').optional().or(z.literal('')),
  dinner_end_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Formato inválido (HH:MM)').optional().or(z.literal('')),
  use_custom_mailer: z.boolean().optional(),
  mailer_from_address: z.string().optional(),
  mailer_from_name: z.string().optional(),
  smtp_address: z.string().optional(),
  smtp_port: z.coerce.number().optional(),
  smtp_username: z.string().optional(),
  smtp_password: z.string().optional(),
  google_place_id: z.string().optional(),
  google_review_link: z.string().url('Debe ser una URL válida').optional().or(z.literal('')),
  average_ticket_eur: z
    .string()
    .regex(/^\d{1,5}([.,]\d{1,2})?$/, 'Introduce un importe en euros, por ejemplo 60 o 60,50')
    .optional()
    .or(z.literal('')),
});

/** Convierte "60,50" en 6050 céntimos; vacío → null (borra el valor) */
function eurosToCents(value: string | undefined): number | null {
  if (!value) return null;
  return Math.round(Number(value.replace(',', '.')) * 100);
}

function centsToEuros(cents: number | null | undefined): string {
  if (!cents) return '';
  const euros = cents / 100;
  return Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ',');
}

type SettingsFormValues = z.infer<typeof settingsSchema>;

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { admin } = useAdminAuth();
  const { refetchRestaurant } = useRestaurant();
  const { trackAdminSettings } = useAnalytics();

  // Check permissions
  const canEdit = admin?.role === 'owner' || admin?.role === 'manager';

  // Fetch restaurant
  const { data: restaurant, isLoading } = useQuery({
    queryKey: ['admin-restaurant'],
    queryFn: () => adminRestaurantService.getRestaurant(),
  });

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      name: '',
      logo_url: '',
      primary_color: '',
      email_menu_title: '',
      email_logo_url: '',
      provider: 'square',
      lunch_start_time: '',
      lunch_end_time: '',
      dinner_start_time: '',
      dinner_end_time: '',
      use_custom_mailer: false,
      mailer_from_address: '',
      mailer_from_name: '',
      smtp_address: '',
      smtp_port: undefined,
      smtp_username: '',
      smtp_password: '',
        google_place_id: '',
        google_review_link: '',
        average_ticket_eur: '',
    },
  });
  const isMailerEnabled = Boolean(form.watch('use_custom_mailer'));
  useEffect(() => {
    let mounted = true;
    adminRestaurantService.getMailerStatus().then((status) => {
      if (!mounted) return;
      // Populate form fields from status
      if (status) {
        form.setValue('use_custom_mailer', status.configured);
        const from = status.from || '';
        const m = /<([^>]+)>/.exec(from);
        form.setValue('mailer_from_address', m ? m[1] : ('') );
        form.setValue('mailer_from_name', from.replace(/<.*>/, '').trim());
        form.setValue('smtp_address', status.settings?.address ?? '');
        form.setValue('smtp_port', status.settings?.port ?? undefined);
        form.setValue('smtp_username', status.settings?.username ?? '');
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, [form]);

  // Sync restaurant data into form when restaurant loads
  useEffect(() => {
    if (restaurant) {
      form.reset({
        name: restaurant.name,
        logo_url: restaurant.logo_url || '',
        primary_color: restaurant.primary_color || '',
        email_menu_title: restaurant.email_menu_title || '',
        email_logo_url: restaurant.email_logo_url || '',
        provider: restaurant.provider,
        lunch_start_time: restaurant.lunch_start_time || '',
        lunch_end_time: restaurant.lunch_end_time || '',
        dinner_start_time: restaurant.dinner_start_time || '',
        dinner_end_time: restaurant.dinner_end_time || '',
        google_place_id: restaurant.google_place_id || '',
        google_review_link: restaurant.google_review_link || '',
        average_ticket_eur: centsToEuros(restaurant.average_ticket_cents),
      });
    }
  }, [restaurant, form]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateRestaurantRequest) =>
      adminRestaurantService.updateRestaurant(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['admin-restaurant'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant'] });
      // Refrescar el restaurantStore para que OrdersPage vea los cambios
      await refetchRestaurant();
      trackAdminSettings.save();
      toast.success('Configuración actualizada correctamente');
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      const message = apiError?.response?.data?.error || 'Error al actualizar la configuración';
      toast.error(message);
    },
  });

  /**
   * Handle form submission
   */
  const onSubmit = (values: SettingsFormValues) => {
    // Frontend domain validation for mailer when using custom mailer
    if (values.use_custom_mailer && values.mailer_from_address && values.smtp_username) {
      const match = domainsMatchFromAndSmtp(values.mailer_from_address, values.smtp_username);
      if (!match) {
        toast.error('El dominio del From Address debe coincidir con el dominio del SMTP Username cuando se usa mailer personalizado');
        return;
      }
    }
    const data: UpdateRestaurantRequest = {
      restaurant: {
        name: values.name,
        logo_url: values.logo_url || undefined,
        primary_color: values.primary_color || undefined,
        email_menu_title: values.email_menu_title || undefined,
        email_logo_url: values.email_logo_url || undefined,
        lunch_start_time: values.lunch_start_time || undefined,
        lunch_end_time: values.lunch_end_time || undefined,
        dinner_start_time: values.dinner_start_time || undefined,
        dinner_end_time: values.dinner_end_time || undefined,
        provider: values.provider,
        use_custom_mailer: values.use_custom_mailer,
        mailer_from_address: values.mailer_from_address || undefined,
        mailer_from_name: values.mailer_from_name || undefined,
        smtp_address: values.smtp_address || undefined,
        smtp_port: values.smtp_port || undefined,
        smtp_username: values.smtp_username || undefined,
        smtp_password: values.smtp_password || undefined,
        google_place_id: values.google_place_id || undefined,
        google_review_link: values.google_review_link || undefined,
        average_ticket_cents: eurosToCents(values.average_ticket_eur),
      },
    };

    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Configuración" />
          <PageLoading variant="form" rows={3} />
        </div>
      </AdminPageShell>
    );
  }

  if (!canEdit) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Configuración" subtitle="Configuración del restaurante" />
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <p className="text-yellow-900">
                Solo los propietarios y gerentes pueden modificar la configuración del restaurante.
              </p>
            </CardContent>
          </Card>
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Configuración"
          subtitle="Administra la configuración de tu restaurante"
        />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Restaurant Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gray-600" />
                  <CardTitle>Información del Restaurante</CardTitle>
                </div>
                <CardDescription>
                  Información básica que se muestra a los clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>Nombre del Restaurante *</FormLabel>
                       <FormControl>
                         <Input placeholder="Ej: Acme Restaurant" className="min-h-11" {...field} />
                       </FormControl>
                      <FormDescription>
                        Nombre que aparecerá en todas las comunicaciones con clientes
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Branding */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Palette className="h-5 w-5 text-gray-600" />
                  <CardTitle>Personalización</CardTitle>
                </div>
                <CardDescription>
                  Logo y colores de tu marca
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo URL */}
                <FormField
                  control={form.control}
                  name="logo_url"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>URL del Logo</FormLabel>
                       <FormControl>
                         <Input
                           type="url"
                           placeholder="https://ejemplo.com/logo.png"
                           className="min-h-11"
                           {...field}
                         />
                       </FormControl>
                      <FormDescription>
                        URL pública de tu logo (se mostrará en los QR impresos)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Primary Color */}
                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>Color Primario</FormLabel>
                       <div className="flex gap-2">
                         <FormControl>
                           <Input
                             placeholder="#b41f3d"
                             className="flex-1 min-h-11"
                             {...field}
                           />
                         </FormControl>
                         <Input
                           type="color"
                           value={field.value || '#b41f3d'}
                           onChange={(e) => field.onChange(e.target.value)}
                           className="w-20 min-h-11"
                         />
                       </div>
                      <FormDescription>
                        Color principal de tu marca en formato hexadecimal
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Logo Preview */}
                {(form.watch('logo_url') || restaurant?.logo_url) && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Vista Previa del Logo
                    </label>
                    <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-center">
                      <img
                        src={form.watch('logo_url') || restaurant?.logo_url || ''}
                        alt="Logo preview"
                        className="max-h-20 object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TPV Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Plug className="h-5 w-5 text-gray-600" />
                  <CardTitle>Integración TPV</CardTitle>
                </div>
                <CardDescription>
                  Configuración del sistema de punto de venta
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Provider */}
                <FormField
                  control={form.control}
                  name="provider"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>Proveedor</FormLabel>
                       <FormControl>
                         <select
                           className="w-full min-h-11 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary bg-gray-50"
                           {...field}
                           disabled
                         >
                          <option value="square">Square</option>
                        </select>
                      </FormControl>
                      <FormDescription>
                        El proveedor TPV no se puede modificar
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Provider Credentials Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Nota:</strong> Las credenciales del TPV se configuran por seguridad
                    directamente en la base de datos. Contacta con soporte técnico si necesitas
                    modificarlas.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Email Configuration */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-gray-600" />
                  <CardTitle>Configuración de Email</CardTitle>
                </div>
                <CardDescription>
                  Personaliza los emails que reciben tus clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Email Menu Title */}
                <FormField
                  control={form.control}
                  name="email_menu_title"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>Título del Menú en Email</FormLabel>
                       <FormControl>
                         <Input
                           placeholder="Ej: Menú Degustación"
                           className="min-h-11"
                           {...field}
                         />
                       </FormControl>
                      <FormDescription>
                        Texto que aparece como título del menú en los emails de recuerdo.
                        Si se deja vacío, se mostrará "Tu experiencia".
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email Logo URL */}
                <FormField
                  control={form.control}
                  name="email_logo_url"
                  render={({ field }) => (
                    <FormItem>
                       <FormLabel>Logo para Emails</FormLabel>
                       <FormControl>
                         <Input
                           type="url"
                           placeholder="https://ejemplo.com/logo.png"
                           className="min-h-11"
                           {...field}
                         />
                       </FormControl>
                      <FormDescription>
                        URL del logo en formato PNG para emails (Gmail no soporta SVG).
                        Si se deja vacío, se usará el logo principal.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-gray-600" />
                  <CardTitle>Mailer del Restaurante</CardTitle>
                </div>
                <CardDescription>
                  Configuración de envío de correo desde el dominio del restaurante
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Usar email del restaurante</label>
                    <input type="checkbox" className="mt-1 h-4 w-4" {...form.register('use_custom_mailer')} />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="mailer_from_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>From Address</FormLabel>
                      <FormControl>
                        <Input placeholder="noreply@restaurante.com" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                      </FormControl>
                      <FormDescription>Dirección de correo del remitente</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailer_from_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>From Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Restaurante" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                      </FormControl>
                      <FormDescription>Nombre mostrado como remitente</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <details className="border rounded-md p-4 bg-white" disabled={!isMailerEnabled}>
                  <summary className="cursor-pointer text-sm font-medium text-gray-700"> SMTP Configuración </summary>
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="smtp_address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SMTP Address</FormLabel>
                          <FormControl>
                            <Input placeholder="smtp.example.com" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="smtp_port"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SMTP Port</FormLabel>
                          <FormControl>
                            <Input type="number" placeholder="587" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="smtp_username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SMTP Username</FormLabel>
                          <FormControl>
                            <Input placeholder="user@example.com" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="smtp_password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SMTP Password</FormLabel>
                          <FormControl>
                            <Input type="password" placeholder="••••••••" className="min-h-11" {...field} disabled={!isMailerEnabled} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </details>

                <div className="flex gap-4 mt-2">
                  <Button type="button" onClick={async () => {
                    if (!isMailerEnabled) {
                      toast.info('Habilita y guarda la configuración del mailer primero');
                      return;
                    }
                    try {
                      await adminRestaurantService.sendTestEmail();
                      toast.success('Email de prueba enviado');
                    } catch (err) {
                      const apiError = err as { response?: { data?: { error?: string } } };
                      toast.error(apiError?.response?.data?.error || 'Error enviando correo de prueba');
                    }
                  }} className="min-h-11" disabled={!isMailerEnabled}>Enviar correo de prueba</Button>
                </div>
              </CardContent>
            </Card>

             <Card>
               <CardHeader>
                 <div className="flex items-center gap-2">
                   <Info className="h-5 w-5 text-gray-600" />
                   <CardTitle>Reseñas de Google</CardTitle>
                 </div>
                 <CardDescription>
                   Configura el enlace para que los clientes dejen reseñas
                 </CardDescription>
               </CardHeader>
               <CardContent className="space-y-4">
                 <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                   <div className="flex items-start gap-3">
                     <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                     <div className="text-sm text-blue-900">
                       <p className="font-medium mb-1">¿Cómo funcionan las reseñas de Google?</p>
                       <p className="mb-2">
                         Puedes configurar <strong>uno o ambos</strong> campos. El sistema priorizará el link directo si está disponible, ya que ofrece una mejor experiencia al cliente.
                       </p>
                       <ul className="list-disc list-inside space-y-1 text-blue-800">
                         <li><strong>Link directo (recomendado):</strong> Abre directamente el formulario de reseña. Menos pasos = más reseñas.</li>
                         <li><strong>Place ID:</strong> Abre tu ficha en Google Maps. El cliente debe buscar el botón "Escribir opinión".</li>
                       </ul>
                     </div>
                   </div>
                 </div>

                 <FormField
                   control={form.control}
                   name="google_review_link"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Link directo de reseña (recomendado)</FormLabel>
                       <FormControl>
                         <Input
                           placeholder="https://g.page/r/XXXX/review"
                           className="min-h-11"
                           {...field}
                         />
                       </FormControl>
                       <FormDescription>
                         <strong>Mejor opción.</strong> El cliente llega directamente al formulario de reseña sin pasos intermedios.
                         <br />
                         <span className="text-muted-foreground">Para obtenerlo: Google Business Profile → Más → Promoción → "Obtener más reseñas"</span>
                       </FormDescription>
                       <FormMessage />
                     </FormItem>
                   )}
                 />

                 <div className="relative">
                   <div className="absolute inset-0 flex items-center">
                     <span className="w-full border-t" />
                   </div>
                   <div className="relative flex justify-center text-xs uppercase">
                     <span className="bg-white px-2 text-muted-foreground">o usa el Place ID como alternativa</span>
                   </div>
                 </div>

                 <FormField
                   control={form.control}
                   name="google_place_id"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Google Place ID (alternativa)</FormLabel>
                       <FormControl>
                         <Input
                           placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4"
                           className="min-h-11"
                           {...field}
                         />
                       </FormControl>
                       <FormDescription>
                         <strong>Funciona sin configuración especial.</strong> Busca tu restaurante en Google Maps, copia la URL y extrae el ID después de <code>place_id=</code>.
                         <br />
                         <span className="text-muted-foreground">Si el link directo está configurado, este campo se ignora.</span>
                       </FormDescription>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
               </CardContent>
             </Card>

             <Card>
               <CardHeader>
                 <div className="flex items-center gap-2">
                   <Euro className="h-5 w-5 text-gray-600" />
                   <CardTitle>Impacto económico</CardTitle>
                 </div>
                 <CardDescription>
                   Para estimar en el dashboard el valor de las visitas repetidas cuando no hay importe del TPV
                 </CardDescription>
               </CardHeader>
               <CardContent className="space-y-4">
                 <FormField
                   control={form.control}
                   name="average_ticket_eur"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel>Ticket medio por mesa (€, orientativo)</FormLabel>
                       <FormControl>
                         <Input inputMode="decimal" placeholder="60" className="min-h-11 max-w-[200px]" {...field} />
                       </FormControl>
                       <FormDescription>
                         Lo que gasta de media una mesa. Solo se usa para estimaciones, que el dashboard marca siempre como estimadas.
                         {restaurant?.tpv_average_ticket_cents ? (
                           <>
                             <br />
                             <span className="text-muted-foreground">
                               Referencia: según tu TPV, el ticket medio de las mesas con recuerdo es{' '}
                               {(restaurant.tpv_average_ticket_cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}.
                             </span>
                           </>
                         ) : null}
                       </FormDescription>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
               </CardContent>
             </Card>

             <Card>
               <CardHeader>
                 <div className="flex items-center gap-2">
                   <Clock className="h-5 w-5 text-gray-600" />
                   <CardTitle>Horarios de Servicio</CardTitle>
                </div>
                <CardDescription>
                  Configura los turnos de comida y cena para filtrar órdenes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="lunch_start_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Inicio Comida</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            className="min-h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lunch_end_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fin Comida</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            className="min-h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="dinner_start_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Inicio Cena</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            className="min-h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dinner_end_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fin Cena</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            className="min-h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Ejemplo: Comida 12:00 - 16:00, Cena 20:00 - 00:00
                </p>
              </CardContent>
             </Card>

            <div className="flex gap-4">
              <AdminButton
                type="submit"
                variant="primary"
                isLoading={updateMutation.isPending}
                loadingText="Guardando..."
                className="flex-1 md:flex-initial"
              >
                <Save className="mr-2 h-4 w-4" />
                Guardar Cambios
              </AdminButton>
              <AdminButton
                type="button"
                variant="ghost"
                onClick={() => form.reset()}
                disabled={updateMutation.isPending}
              >
                Cancelar
              </AdminButton>
            </div>
          </form>
        </Form>
      </div>
    </AdminPageShell>
  );
}
